import { useQuery } from '@tanstack/react-query';

import type { ApplicationTabId } from './application-query.constants';
import type {
  DealsDashboardTab,
  DealsStatusCounts,
  RenewalStatusCounts,
} from './application.types';
import { getTabScopeFilter } from './deals-dashboard.api';
import { apiUri } from '@/constants/Constants';
import { DealsDashboardClient } from '@/shared/api';

export type UseDealsStatusCountsParams = {
  applicationTab: ApplicationTabId;
  enabled?: boolean;
  /**
   * Expiring Soon's "View expired deals" toggle. The renewal buckets are counted over that
   * tab's population server-side, so the toggle has to reach them too — otherwise the pills
   * tally the default 45-day window while the table they filter shows older bonds.
   */
  includeExpiredDeals?: boolean;
};

export type DealsCounts = {
  /** Deal status pill counts. Absent on the expiring-soon tab, which renders renewal pills. */
  count_by_status?: DealsStatusCounts;
  /**
   * Renewal pill counts, grouped by renewal opportunity status and scoped server-side to the
   * expiring-soon population. Only the expiring-soon tab is served these. Absent when the renewal
   * rollout is off for the landlord or when the counting service is degraded — the two are
   * indistinguishable and both mean "unavailable".
   */
  count_by_renewal_status?: RenewalStatusCounts;
};

const createStatusCountsQuery = (
  applicationTab: DealsDashboardTab,
  includeExpiredDeals: boolean,
) => ({
  filters: {
    ...getTabScopeFilter(applicationTab, includeExpiredDeals),
  },
});

export const getDealsStatusCounts = async (
  applicationTab: DealsDashboardTab,
  includeExpiredDeals = false,
): Promise<DealsCounts> => {
  const payloadObject = createStatusCountsQuery(applicationTab, includeExpiredDeals);

  const response = await DealsDashboardClient.post<{ data: DealsCounts }>(
    apiUri.getDealsStatusCounts,
    payloadObject,
  );

  return response.data.data;
};

export const useDealsStatusCounts = ({
  applicationTab,
  enabled = true,
  includeExpiredDeals = false,
}: UseDealsStatusCountsParams) =>
  useQuery<DealsCounts>({
    // The toggle is part of the key: under `staleTime: Infinity` a changed request alone
    // would never refetch, so the pills would keep serving the pre-toggle figures.
    queryKey: ['deals-status-counts', applicationTab, includeExpiredDeals],
    queryFn: () => getDealsStatusCounts(applicationTab, includeExpiredDeals),
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
